import { memberSessionStore } from "../../app/runtime";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import type { MemberRouteMetadata } from "../../app/routes";
import { publicApi } from "../shared/api";
import { saveBlob } from "../shared/api";
import { Button, EmptyState, ErrorState, LoadingState, PageHeader, Surface, navigate } from "../shared/ui";
import { PublicHomeReference } from "../../design-review/PublicHomeReference";
import { LegalDocument } from "../shared/MemberLayouts";
import { isPublicPrivacyRequest } from "./public-document";

const marketingCopy: Readonly<Record<string, { readonly title: string; readonly lead: string; readonly points: readonly string[] }>> = {
  "PUB-S01": {
    title: "생활에 필요한 거래를 더 안전하게",
    lead: "서류를 제출하면 CarePass가 확인하고, 승인된 금액을 결제한 뒤 원하는 일정에 송금합니다.",
    points: ["월 한도 안에서 거래 접수", "관리자 심사와 보완 요청", "카드 결제와 송금 결과 확인"],
  },
  "PUB-S02": {
    title: "결제와 송금 사이를 책임 있게 연결합니다",
    lead: "거래 서류와 수취 계좌를 확인한 뒤 승인된 금액만 결제하고 송금합니다.",
    points: ["거래마다 필요한 증빙 안내", "결제 전 승인 금액 확인", "결제·송금 상태를 따로 추적"],
  },
  "PUB-S03": {
    title: "가입부터 송금까지 네 단계",
    lead: "일반 가입이나 제휴 가입을 마치면 거래 접수, 심사, 결제, 송금 순서로 진행됩니다.",
    points: ["1. 요금제 선택과 본인확인", "2. 거래와 증빙 제출", "3. 심사 결과 확인과 결제", "4. 즉시 또는 지정일 송금"],
  },
  "PUB-S05": {
    title: "예상하지 못한 상황을 위한 혜택지원",
    lead: "대상 여부와 증빙을 확인한 뒤 승인과 지급 결과를 안내합니다.",
    points: ["신청 자격과 중복 이력 확인", "담당자 기안과 별도 승인", "지급 완료 기록 확인"],
  },
};

function MarketingPage({ route }: { readonly route: MemberRouteMetadata }) {
  const copy = marketingCopy[route.id] ?? marketingCopy["PUB-S01"]!;
  return (
    <div className="public-page">
      <section className="public-hero">
        <p className="eyebrow">CarePass</p>
        <h1>{copy.title}</h1>
        <p>{copy.lead}</p>
        <div className="public-hero__actions">
          <Button size="large" onClick={() => navigate("/signup")}>가입 시작</Button>
          <Button size="large" variant="secondary" onClick={() => navigate("/login")}>회원 로그인</Button>
        </div>
      </section>
      <section className="public-points" aria-label="주요 내용">
        {copy.points.map((point, index) => (
          <article key={point}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <h2>{point}</h2>
          </article>
        ))}
      </section>
    </div>
  );
}

function PublicHomePage() {
  const plans = useQuery({ queryKey: ["public", "plans"], queryFn: publicApi.plans });
  return <PublicHomeReference onNavigate={navigate} plans={plans.data} />;
}

function ContentListPage({ type, description }: {
  readonly type: string;
  readonly title: string;
  readonly description: string;
}) {
  const contents = useQuery({ queryKey: ["public", "contents", type], queryFn: () => publicApi.contents(type) });
  return (
    <div>
      <nav className="member-inquiry-tabs" aria-label="공지 및 도움말"><button type="button" aria-current={type === "NOTICE" ? "page" : undefined} onClick={() => navigate("/notices")}>공지사항</button><button type="button" aria-current={type === "FAQ" ? "page" : undefined} onClick={() => navigate("/faq")}>자주 묻는 질문</button></nav><p className="member-reading-intro">{description}</p>
      {contents.isPending ? <LoadingState /> : contents.isError ? (
        <ErrorState error={contents.error} onRetry={() => void contents.refetch()} />
      ) : contents.data.length === 0 ? (
        <EmptyState title="게시된 내용이 없습니다" description="새로운 내용이 등록되면 이곳에서 확인할 수 있습니다." />
      ) : type === "FAQ" ? <div className="member-faq-list">{contents.data.map((content) => <details key={content.id}><summary>{content.title}</summary><p className="rich-copy">{content.content}</p></details>)}</div> : (
        <div className="content-list">
          {contents.data.map((content) => (
            <button key={content.id} type="button" onClick={() => navigate(`/notices/${content.id}`)}>
              <span>{type === "FAQ" ? "자주 묻는 질문" : "공지"}</span>
              <strong>{content.title}</strong>
              <small>{content.publishedAt?.slice(0, 10) ?? content.createdAt.slice(0, 10)}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ContentDetail({ id }: { readonly id: number }) {
  const content = useQuery({ queryKey: ["public", "content", id], queryFn: () => publicApi.content(id), enabled: Number.isFinite(id) });
  if (content.isPending) return <LoadingState />;
  if (content.isError) return <ErrorState error={content.error} onRetry={() => void content.refetch()} />;
  return (
    <article className="content-detail">
      <button className="text-link" type="button" onClick={() => navigate("/notices")}>공지 목록</button>
      <PageHeader eyebrow={content.data.contentType === "FAQ" ? "자주 묻는 질문" : "공지사항"} title={content.data.title} description={content.data.publishedAt?.slice(0, 10)} />
      <Surface><div className="rich-copy">{content.data.content}</div></Surface>
      {content.data.attachments && content.data.attachments.length > 0 && (
        <Surface title="첨부파일">
          <div className="attachment-list">
            {content.data.attachments.map((attachment) => (
              <div key={attachment.fileAssetId}>
                <span><strong>{attachment.originalFilename ?? `첨부파일 ${attachment.fileAssetId}`}</strong><small>{attachment.contentType ?? "파일"}{attachment.fileSize ? ` · ${formatBytes(attachment.fileSize)}` : ""}</small></span>
                <Button size="small" variant="secondary" disabled={!attachment.downloadable} onClick={async () => {
                  const blob = await publicApi.contentAttachment(id, attachment.fileAssetId);
                  saveBlob(blob, attachment.originalFilename ?? `carepass-attachment-${attachment.fileAssetId}`);
                }}>{attachment.downloadable ? "다운로드" : "다운로드 불가"}</Button>
              </div>
            ))}
          </div>
        </Surface>
      )}
    </article>
  );
}

function TermDetail({ id }: { readonly id: number }) {
  const term = useQuery({ queryKey: ["public", "term", id], queryFn: () => publicApi.term(id), enabled: Number.isFinite(id) });
  if (term.isPending) return <LoadingState label="약관을 불러오는 중입니다." />;
  if (term.isError) return <ErrorState error={term.error} onRetry={() => void term.refetch()} />;
  return <LegalDocument term={term.data} showAgreementRequirement={term.data.termCode !== "PRIVACY_POLICY"} />;
}

export function LatestTerm() {
  const privacy = isPublicPrivacyRequest(window.location.search);
  const code = privacy ? "PRIVACY_POLICY" : "SERVICE_TERMS";
  // The server chooses the effective version; re-entering reads freshly published changes.
  const terms = useQuery({ queryKey: ["public", "latest-term", code], queryFn: () => publicApi.terms(code), staleTime: 0, refetchOnMount: "always" });
  if (terms.isPending || terms.isFetching) return <LoadingState label={privacy ? "개인정보처리방침을 불러오는 중입니다." : "약관을 불러오는 중입니다."}/>;
  if (terms.isError) return <ErrorState error={terms.error} onRetry={() => void terms.refetch()}/>;
  if (!terms.data[0] || terms.data[0].termCode !== code) return <EmptyState title={privacy ? "게시된 개인정보처리방침이 없습니다" : "게시된 약관이 없습니다"} description="고객센터 1522-5663으로 문의해 주세요."/>;
  return <LegalDocument term={terms.data[0]} showAgreementRequirement={false}/>;
}

function PublicRedirect({ to }: { readonly to: string }) {
  useEffect(() => { navigate(to, true); }, [to]);
  return <LoadingState label="요청한 위치로 이동하고 있습니다." />;
}

export function PublicPage({ route, params }: {
  readonly route: MemberRouteMetadata;
  readonly params: Readonly<Record<string, string>>;
}) {
  if (route.id === "PUB-S01") return <PublicHomePage />;
  if (route.id === "PUB-S02") return <PublicRedirect to="/#features" />;
  if (route.id === "PUB-S03") return <PublicRedirect to="/#how" />;
  if (route.id === "PUB-S04") return <PublicRedirect to="/#pricing" />;
  if (route.id === "PUB-S05") return <PublicRedirect to="/#benefit" />;
  if (route.id === "PUB-S08") return <PublicRedirect to={memberSessionStore.get() ? "/app/inquiries" : "/login?returnTo=%2Fapp%2Finquiries"} />;
  if (route.id === "PUB-S06") return <ContentListPage type="NOTICE" title="공지사항" description="서비스 변경과 점검 일정을 확인하세요." />;
  if (route.id === "PUB-S07") return <ContentListPage type="FAQ" title="자주 묻는 질문" description="궁금한 내용을 먼저 확인해 보세요." />;
  if (route.id === "PUB-S06-DETAIL") return <ContentDetail id={Number(params.contentId)} />;
  if (route.id === "PUB-S09") return <TermDetail id={Number(params.termVersionId)} />;
  if (route.id === "PUB-S09-LATEST") return <LatestTerm />;
  return <MarketingPage route={route} />;
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
